import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import SignaturePad from "../components/SignaturePad";

const PUB = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

const INFO_FIELDS = [
  ["id_number", "ID / passport number *", "text"],
  ["phone", "Phone number *", "tel"],
  ["address", "Home address", "text"],
  ["bank_name", "Bank", "text"],
  ["account_number", "Account number", "text"],
  ["branch_code", "Branch code", "text"],
  ["emergency_contact_name", "Emergency contact — name", "text"],
  ["emergency_contact_phone", "Emergency contact — phone", "tel"],
];

export default function PublicOnboarding() {
  const { token } = useParams();
  const [view, setView] = useState(null); // null loading, false error
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [sig, setSig] = useState(null);
  const [info, setInfo] = useState({});
  const [accountType, setAccountType] = useState("Cheque");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");

  const load = useCallback(() => {
    PUB.get(`/public/onboarding/${token}`)
      .then((r) => { setView(r.data); setInfo(r.data.prefill || {}); setAccountType(r.data.prefill?.account_type || "Cheque"); })
      .catch((e) => { setErr(e.response?.data?.detail || "This link is invalid or has expired"); setView(false); });
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async (path, body, okMsg) => {
    setBusy(true);
    try {
      await PUB.post(`/public/onboarding/${token}/${path}`, body);
      setFlash(okMsg);
      setSig(null);
      setName("");
      load();
    } catch (e) {
      setFlash("");
      setErr(e.response?.data?.detail || "Something went wrong — please try again");
    } finally {
      setBusy(false);
    }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-[#07050A] text-[#EDEDF2] px-4 py-10" data-testid="pub-onboarding">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <p className="font-heading text-2xl font-bold tracking-tight">MISE</p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground/70">Prepared Intelligence</p>
        </div>
        {flash && <div className="rounded-md bg-[#A9C97E]/10 text-[#A9C97E] px-4 py-3 text-sm" data-testid="pub-flash">{flash}</div>}
        {err && <div className="rounded-md bg-[#F87171]/10 text-[#F87171] px-4 py-3 text-sm" data-testid="pub-error">{err}</div>}
        {children}
      </div>
    </div>
  );

  if (view === null) return shell(<p className="text-muted-foreground">Loading your onboarding…</p>);
  if (view === false) return shell(null);

  const fullName = `${view.first_name} ${view.last_name}`.trim();

  if (["sign_offer", "sign_contract"].includes(view.action)) {
    const isOffer = view.action === "sign_offer";
    return shell(
      <>
        <div>
          <h1 className="font-heading text-xl font-bold">{view.doc_title}</h1>
          <p className="text-sm text-muted-foreground mt-1">Hi {view.first_name} — please review your {isOffer ? "offer letter" : "employment contract"}, then sign below.</p>
        </div>
        <div className="bg-white text-[#1A1A1A] rounded-lg p-6 sm:p-8 shadow-xl max-h-[55vh] overflow-y-auto" data-testid="pub-doc">
          <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed">{view.doc_text}</pre>
        </div>
        <div className="space-y-3 rounded-lg border border-border bg-[#151226] p-5">
          <div className="space-y-1.5">
            <Label className="text-xs">Full legal name (must match the document)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={fullName} data-testid="pub-sign-name" />
          </div>
          <SignaturePad onChange={setSig} testid="pub-sig" />
          <Button disabled={busy || !name.trim() || !sig} data-testid="pub-sign-submit"
            className="w-full bg-primary hover:bg-[#8B5CF6] text-white"
            onClick={() => submit(isOffer ? "sign-offer" : "sign-contract", { name: name.trim(), signature_data: sig },
              isOffer ? "Offer signed — please complete your personal details below." : "Contract signed — thank you.")}>
            {busy ? "Signing…" : `Sign ${isOffer ? "offer letter" : "contract"}`}
          </Button>
          <p className="text-[11px] text-muted-foreground">Your signature, name, timestamp and device details are recorded as the legal audit trail (ECT Act).</p>
        </div>
      </>
    );
  }

  if (view.action === "submit_info") {
    return shell(
      <>
        <div>
          <h1 className="font-heading text-xl font-bold">Your personal details</h1>
          <p className="text-sm text-muted-foreground mt-1">Hi {view.first_name} — please complete your employee profile. Banking details are stored encrypted.</p>
        </div>
        <div className="space-y-3 rounded-lg border border-border bg-[#151226] p-5">
          {view.sensitive_on_file && (
            <p className="text-xs text-[#A9C97E] bg-[#A9C97E]/10 rounded-md px-3 py-2" data-testid="pub-sensitive-note">
              Your previously submitted ID/banking details are stored encrypted — leave those fields blank to keep them, or enter new values to update.
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {INFO_FIELDS.map(([k, label, type]) => (
              <div className="space-y-1.5" key={k}>
                <Label className="text-xs">{label}</Label>
                <Input type={type} value={info[k] || ""} onChange={(e) => setInfo({ ...info, [k]: e.target.value })} data-testid={`pub-info-${k}`} />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label className="text-xs">Account type</Label>
              <Select value={accountType} onValueChange={setAccountType}>
                <SelectTrigger data-testid="pub-info-account_type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cheque">Cheque / Current</SelectItem>
                  <SelectItem value="Savings">Savings</SelectItem>
                  <SelectItem value="Transmission">Transmission</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button disabled={busy} data-testid="pub-info-submit" className="w-full bg-primary hover:bg-[#8B5CF6] text-white"
            onClick={() => submit("info", { ...info, account_type: accountType }, "Details submitted — thank you.")}>
            {busy ? "Submitting…" : "Submit my details"}
          </Button>
        </div>
      </>
    );
  }

  const DONE = {
    wait_manager: ["Details received", "The venue will now prepare your employment contract — you'll get an email when it's ready to sign."],
    wait_employer: ["Contract signed", "Thank you — the venue will countersign and activate your profile."],
    complete: ["All done", "Your onboarding is complete. Welcome aboard."],
  };
  const [title, msg] = DONE[view.action] || DONE.complete;
  return shell(
    <div className="rounded-lg border border-border bg-[#151226] p-8 text-center space-y-3" data-testid="pub-done">
      <CheckCircle size={40} className="text-[#A9C97E] mx-auto" />
      <h1 className="font-heading text-xl font-bold">{title}</h1>
      <p className="text-sm text-muted-foreground">{msg}</p>
    </div>
  );
}
